import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import { buildArtifactDocument } from '../../artifactPage/htmlShell';
import { artifactPagePath } from '../../artifactPage/artifactPageUrl';
import css from './ArtifactActions.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.ARTIFACT_SHELF';
const MAX_FILE_NAME_LENGTH = 80;

const toFileName = title =>
    `${title
        .replace(/[\\/:*?"<>|\r\n\t]/g, ' ')
        .trim()
        .slice(0, MAX_FILE_NAME_LENGTH) || 'artifact'}.html`;

// Everything you can do to the document the shelf has open. The download builds
// its own file here rather than taking a handler, so the shelf is left with
// fetching and layout.
const ArtifactActions = ({ artifact = null, content = null, isMaximized, onToggleMaximized, onClose, translate }) => {
    const handleDownload = useCallback(() => {
        if (!content) return;
        // The shell travels with the fragment, so the saved file opens standalone
        // under the same CSP the shelf renders it with.
        const url = URL.createObjectURL(new Blob([buildArtifactDocument(content)], { type: 'text/html' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = toFileName(artifact?.title || translate(`${TRANSLATION_PREFIX}.TITLE_FALLBACK`));
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }, [content, artifact, translate]);

    return (
        <div className={css.actions}>
            <Icon
                name={isMaximized ? 'arrows-diagonal-minimize-2' : 'arrows-diagonal'}
                size={IconSizes.SMALL}
                onClick={onToggleMaximized}
                hoverText={`${TRANSLATION_PREFIX}.${isMaximized ? 'MINIMIZE' : 'MAXIMIZE'}`}
            />
            <a
                className={css.actionLink}
                href={artifactPagePath(artifact?.contentUrl)}
                target="_blank"
                rel="noopener noreferrer"
            >
                <Icon name="external-link" size={IconSizes.SMALL} hoverText={`${TRANSLATION_PREFIX}.NEW_TAB`} />
            </a>
            <Icon
                name="file-download"
                size={IconSizes.SMALL}
                disabled={!content}
                onClick={handleDownload}
                hoverText={`${TRANSLATION_PREFIX}.DOWNLOAD`}
            />
            <Icon name="x" size={IconSizes.X_SMALL} onClick={onClose} hoverText={`${TRANSLATION_PREFIX}.CLOSE`} />
        </div>
    );
};

ArtifactActions.propTypes = {
    artifact: PropTypes.shape({
        title: PropTypes.string,
        contentUrl: PropTypes.string,
    }),
    content: PropTypes.string,
    isMaximized: PropTypes.bool,
    onToggleMaximized: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(ArtifactActions);
